import { Checkbox, Grid, GridColumn, GridRow, Input, ModalBottomButtons } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { apiHelper } from '../../../utils/apiHelper';
import { showSuccessUpdateToast, showUserDbErrorToast } from '../../../utils/toast';

type MonitorUserFormValues = {
  username: string;
  password: string;
  monitorEnabled: boolean;
  pushEnabled: boolean;
  allowPasswordReset: boolean;
};

type SubstitutionAppUserFormProps = {
  onClose?: () => void;
  onLoad?: (isLoading: boolean) => void;
  initialValues?: Partial<MonitorUserFormValues>;
};

export const SubstitutionAppUserForm = ({ onClose, onLoad, initialValues }: SubstitutionAppUserFormProps) => {
  const { t } = useTranslation();
  const formInitialValues: MonitorUserFormValues = {
    username: initialValues?.username || '',
    password: '',
    monitorEnabled: initialValues?.monitorEnabled ?? true,
    pushEnabled: initialValues?.pushEnabled ?? true,
    allowPasswordReset: false,
  };

  async function handleSubmit(values: MonitorUserFormValues, formHelpers: FormikHelpers<MonitorUserFormValues>) {
    onLoad && onLoad(true);

    try {
      await apiHelper({
        method: 'POST',
        path: '/app/users',
        data: JSON.stringify(values),
        type: 'json',
      });

      showSuccessUpdateToast();
      formHelpers.resetForm();
      onClose && onClose();
    } catch (err) {
      console.error('Failed to create monitor user:', err);
      showUserDbErrorToast();
    } finally {
      onLoad && onLoad(false);
    }
  }

  return (
    <Formik initialValues={formInitialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue, dirty }) => (
        <Form>
          <Grid useFormGap>
            <GridRow>
              <GridColumn width={6}>
                <Input
                  name='username'
                  label={t('substitutions.app.username')}
                  value={values.username}
                  disabled={true}
                />
              </GridColumn>
              <GridColumn width={6}>
                <Input
                  name='password'
                  label={t('substitutions.app.password')}
                  value={values.password}
                  onChange={(e) => setFieldValue('password', e.target.value)}
                />
              </GridColumn>
            </GridRow>

            <GridRow>
              <GridColumn width={4}>
                <Checkbox
                  name='monitorEnabled'
                  label={t('substitutions.app.enableMonitoring')}
                  checked={values.monitorEnabled}
                  onChange={(e) => setFieldValue('monitorEnabled', e.target.checked)}
                />
              </GridColumn>
              <GridColumn width={4}>
                <Checkbox
                  name='pushEnabled'
                  label={t('substitutions.app.enablePushNotifications')}
                  checked={values.pushEnabled}
                  onChange={(e) => setFieldValue('pushEnabled', e.target.checked)}
                />
              </GridColumn>
            </GridRow>
          </Grid>

          <ModalBottomButtons closeButton={{ callback: onClose }} submitButton={{ disabled: !dirty }} />
        </Form>
      )}
    </Formik>
  );
};
